import React, { useState } from 'react';
import { Phone, Check, MapPin, Clock, Shield, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';

const ValorFuneralRJ = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    bairro: '',
    velorio: 'com'
  });

  const whatsappNumber = '552141064022';
  const phoneNumber = '(21) 4106-4022';

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const trackEvent = (eventName, data = {}) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...data
      });
    }
  };

  const handleWhatsAppClick = (package_name = 'geral') => {
    trackEvent('whatsapp_click', { package: package_name });
    let message = 'Olá, vim do Google e gostaria de informações sobre valores de funeral.';
    
    if (package_name === 'direto') {
      message = 'Olá, gostaria de um orçamento para Sepultamento Direto (R$ 1.599).';
    } else if (package_name === 'velorio') {
      message = 'Olá, gostaria de um orçamento para Funeral com Velório (R$ 1.927).';
    } else if (package_name === 'jazigo') {
      message = 'Olá, gostaria de um orçamento para Funeral + Jazigo Social (a partir de R$ 3.492).';
    }
    
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handlePhoneClick = () => {
    trackEvent('phone_click');
    window.location.href = `tel:${whatsappNumber}`;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    trackEvent('form_submit', formData);
    
    const message = `Olá, vim do Google.\n\nNome: ${formData.nome}\nTelefone: ${formData.telefone}\nBairro: ${formData.bairro}\nVelório: ${formData.velorio === 'com' ? 'Com velório' : 'Sepultamento direto'}\n\nGostaria de receber um orçamento.`;
    
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const faqs = [
    {
      question: 'Quanto custa um funeral no Rio de Janeiro?',
      answer: 'O valor do funeral no Rio de Janeiro varia de acordo com o tipo de serviço. O sepultamento direto (sem velório) custa a partir de R$ 1.599,00. O funeral completo com velório começa em R$ 1.927,00. Já o funeral com jazigo social inicia em R$ 3.492,00.'
    },
    {
      question: 'Qual é o valor de um enterro simples?',
      answer: 'O enterro simples (sepultamento direto) tem o valor de R$ 1.599,00 à vista. Inclui caixão, remoção, preparação, ornamentação básica, véu, manto e transporte até o cemitério. Não inclui velório.'
    },
    {
      question: 'O valor do cemitério está incluído?',
      answer: 'Não. O cemitério cobra separadamente pela sepultura. O jazigo social custa R$ 1.565,00, e a gaveta tradicional R$ 2.361,00. A capela também é paga à parte, com valor variável conforme o cemitério escolhido.'
    },
    {
      question: 'Qual a diferença entre jazigo social e gaveta?',
      answer: 'O jazigo social é uma opção mais acessível (R$ 1.565,00) oferecida por alguns cemitérios públicos. A gaveta tradicional (R$ 2.361,00) é uma sepultura convencional. Ambos têm aluguel pelo período de 3 anos, podendo ser renovado.'
    },
    {
      question: 'É possível parcelar o serviço funerário?',
      answer: 'Sim. Aceitamos pagamento à vista ou parcelado no cartão de crédito. Por exemplo, o funeral com velório pode ser pago R$ 1.927,00 à vista ou R$ 2.600,00 no cartão.'
    },
    {
      question: 'O que está incluído no funeral com velório?',
      answer: 'Inclui: caixão padrão, remoção, higienização, conservação, tanatopraxia com ATA, ornamentação, véu, manto, serviços de laboratório, remoção para capela e agendamento do sepultamento.'
    },
    {
      question: 'A funerária realiza atendimento durante a madrugada?',
      answer: 'Sim! Nosso atendimento é 24 horas, todos os dias, incluindo finais de semana, feriados e madrugadas. Entre em contato pelo WhatsApp ou telefone a qualquer momento.'
    },
    {
      question: 'Quais documentos são necessários?',
      answer: 'São necessários: declaração de óbito (emitida pelo hospital, IML ou médico), documento de identidade do falecido, CPF, e documento de quem está contratando o serviço. Nossa equipe orienta sobre toda a documentação.'
    },
    {
      question: 'O que pode alterar o valor do funeral?',
      answer: 'O valor pode variar conforme: cemitério escolhido, local e horário da remoção, peso e altura do falecido, condições do corpo, distância, serviços adicionais (flores, músico, etc). O orçamento final é apresentado antes da contratação.'
    },
    {
      question: 'Como solicitar um orçamento imediato?',
      answer: 'Clique no botão do WhatsApp ou ligue para (21) 97230-0064. Informe o bairro, local do falecimento e se deseja velório. Receba o orçamento completo em poucos minutos, 24 horas por dia.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FuneralHome",
          "name": "Golden Pax Rio - Serviço Funerário",
          "description": "Funerária 24 horas no Rio de Janeiro com valores transparentes",
          "telephone": "+552197230-0064",
          "priceRange": "R$ 1.599 - R$ 3.500",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Rio de Janeiro",
            "addressRegion": "RJ",
            "addressCountry": "BR"
          },
          "areaServed": "Rio de Janeiro",
          "openingHours": "Mo-Su 00:00-23:59",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Serviços Funerários",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Sepultamento Direto",
                  "description": "Sepultamento direto sem velório"
                },
                "price": "1599.00",
                "priceCurrency": "BRL"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Funeral com Velório",
                  "description": "Funeral completo com velório"
                },
                "price": "1927.00",
                "priceCurrency": "BRL"
              }
            ]
          }
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Selo */}
          <div className="inline-flex items-center bg-emerald-700/50 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-emerald-500/30">
            <Clock className="w-4 h-4 mr-2 text-yellow-400" />
            <span className="text-sm font-semibold">Atendimento funerário 24 horas no Rio de Janeiro</span>
          </div>

          {/* H1 */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Valor de Funeral no Rio de Janeiro: veja preços e opções
          </h1>

          {/* Subtítulo */}
          <p className="text-lg sm:text-xl text-emerald-100 mb-8">
            Serviços funerários a partir de <span className="text-yellow-400 font-bold">R$ 1.599,00</span>, com atendimento imediato, orientação completa e orçamento transparente.
          </p>

          {/* Benefícios */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="flex items-center space-x-2 bg-emerald-800/50 px-4 py-3 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <span className="text-sm font-semibold">Atendimento 24 horas</span>
            </div>
            <div className="flex items-center space-x-2 bg-emerald-800/50 px-4 py-3 rounded-lg">
              <Shield className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <span className="text-sm font-semibold">Orçamento detalhado</span>
            </div>
            <div className="flex items-center space-x-2 bg-emerald-800/50 px-4 py-3 rounded-lg">
              <MapPin className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <span className="text-sm font-semibold">Todo o Rio de Janeiro</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => handleWhatsAppClick('geral')}
              className="flex items-center justify-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-emerald-900 px-6 py-4 rounded-lg text-base font-bold transition-all shadow-lg hover:shadow-xl"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Receber orçamento pelo WhatsApp</span>
            </button>
            <button
              onClick={handlePhoneClick}
              className="flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/30 px-6 py-4 rounded-lg text-base font-bold transition-all"
            >
              <Phone className="w-5 h-5" />
              <span>Ligar para atendimento 24h</span>
            </button>
          </div>
        </div>
      </section>

      {/* Seção de Orçamentos */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Quanto custa um funeral no Rio de Janeiro?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              O valor de um funeral depende do tipo de despedida, da necessidade de velório, do cemitério escolhido e dos serviços necessários. Conheça nossas principais opções.
            </p>
          </div>

          {/* Cards de Orçamento */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Card 1: Sepultamento Direto */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 hover:border-emerald-500 transition-all">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Sepultamento Direto</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-emerald-700">R$ 1.599</span>
                <span className="text-gray-600">,00 à vista</span>
              </div>
              <p className="text-gray-600 mb-6">
                Opção econômica para sepultamento direto, sem velório.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  'Caixão padrão',
                  'Remoção do local do falecimento',
                  'Higienização e preparação',
                  'Ornamentação básica',
                  'Véu e manto',
                  'Serviços de laboratório',
                  'Agendamento do sepultamento',
                  'Transporte até o cemitério'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  trackEvent('package_select', { package: 'direto' });
                  handleWhatsAppClick('direto');
                }}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-3 rounded-lg font-bold transition-all"
              >
                Solicitar este orçamento
              </button>
            </div>

            {/* Card 2: Funeral com Velório - DESTAQUE */}
            <div className="bg-white rounded-xl shadow-2xl p-6 border-4 border-yellow-500 hover:border-yellow-600 transition-all relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-500 text-emerald-900 px-4 py-1 rounded-full text-sm font-bold">
                  Mais escolhido
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 mt-2">Funeral com Velório</h3>
              <div className="mb-2">
                <span className="text-4xl font-bold text-emerald-700">R$ 1.927</span>
                <span className="text-gray-600">,00 à vista</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Ou R$ 2.600,00 no cartão de crédito
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  'Caixão padrão',
                  'Remoção do local do falecimento',
                  'Higienização e preparação',
                  'Conservação para o velório',
                  'Tanatopraxia com ATA',
                  'Ornamentação básica',
                  'Véu e manto',
                  'Serviços de laboratório',
                  'Remoção para a capela',
                  'Agendamento do sepultamento'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  trackEvent('package_select', { package: 'velorio' });
                  handleWhatsAppClick('velorio');
                }}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-emerald-900 py-3 rounded-lg font-bold transition-all shadow-lg"
              >
                Solicitar este orçamento
              </button>
            </div>

            {/* Card 3: Funeral + Jazigo Social */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 hover:border-emerald-500 transition-all">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Funeral + Jazigo Social</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-emerald-700">R$ 3.492</span>
                <span className="text-gray-600">,00</span>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                Composição transparente:
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg mb-4 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-700">Serviço funerário com velório</span>
                  <span className="font-bold text-gray-900">R$ 1.927</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-700">Jazigo Social no cemitério</span>
                  <span className="font-bold text-gray-900">R$ 1.565</span>
                </div>
                <div className="border-t border-emerald-200 mt-2 pt-2 flex justify-between">
                  <span className="font-bold text-gray-900">Total inicial</span>
                  <span className="font-bold text-emerald-700">R$ 3.492</span>
                </div>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <strong>Importante:</strong> São dois pagamentos separados: um para a funerária e outro diretamente ao cemitério.
                </p>
              </div>
              <p className="text-xs text-gray-600 mb-6">
                * O valor da capela não está incluído e varia de acordo com o cemitério escolhido.
              </p>
              <button
                onClick={() => {
                  trackEvent('package_select', { package: 'jazigo' });
                  handleWhatsAppClick('jazigo');
                }}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-3 rounded-lg font-bold transition-all"
              >
                Receber orçamento completo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Transparência dos Valores */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Entenda o valor do enterro e do sepultamento
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            O orçamento pode envolver dois pagamentos. O primeiro corresponde aos serviços da funerária, como caixão, remoção, preparação, conservação e transporte. O segundo é pago diretamente ao cemitério e corresponde à sepultura, capela e eventuais taxas.
          </p>

          <div className="bg-emerald-50 rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Opções de cemitério:</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Jazigo Social</span>
                <span className="font-bold text-emerald-700">R$ 1.565,00</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Gaveta tradicional</span>
                <span className="font-bold text-emerald-700">R$ 2.361,00</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Capela</span>
                <span className="font-bold text-gray-600">Valor variável</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              * Aluguel da sepultura pelo período de 3 anos
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded">
            <p className="text-gray-700">
              <strong>Aviso importante:</strong> Os valores podem variar conforme o cemitério, local do falecimento, condições do corpo, peso, altura, horário da remoção e serviços escolhidos. O valor final será confirmado em um orçamento detalhado antes da contratação.
            </p>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-12 sm:py-16 bg-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            Como solicitar o orçamento
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: '1', text: 'Fale com nossa equipe pelo WhatsApp' },
              { num: '2', text: 'Informe o local do falecimento, bairro, peso e altura aproximados' },
              { num: '3', text: 'Escolha entre sepultamento direto ou funeral com velório' },
              { num: '4', text: 'Receba o orçamento completo antes de autorizar o serviço' }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-emerald-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.num}
                </div>
                <p className="text-gray-700">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Área de Atendimento */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Serviço funerário 24 horas em todo o Rio de Janeiro
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Campo Grande', 'Santa Cruz', 'Guaratiba', 'Bangu', 'Realengo', 'Paciência',
              'Sepetiba', 'Recreio', 'Barra da Tijuca', 'Jacarepaguá', 'Tijuca', 'Méier',
              'Madureira', 'Irajá', 'Penha', 'Centro', 'Zona Oeste', 'Zona Norte', 'Zona Sul',
              'Região Metropolitana'
            ].map((local, index) => (
              <span key={index} className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">
                {local}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Formulário */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              Solicite seu orçamento agora
            </h2>
            <p className="text-gray-600 mb-6 text-center">
              Preencha o formulário e fale diretamente conosco pelo WhatsApp
            </p>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Nome completo</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Seu nome"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Telefone</label>
                <input
                  type="tel"
                  required
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="(21) 99999-9999"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Bairro ou cidade</label>
                <input
                  type="text"
                  required
                  value={formData.bairro}
                  onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Ex: Campo Grande, Bangu, Tijuca..."
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Tipo de serviço</label>
                <select
                  value={formData.velorio}
                  onChange={(e) => setFormData({...formData, velorio: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="com">Com velório</option>
                  <option value="sem">Sepultamento direto (sem velório)</option>
                </select>
              </div>
              <button
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-emerald-900 py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center space-x-2"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Enviar e falar pelo WhatsApp</span>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            Perguntas Frequentes
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 bg-white hover:bg-gray-50 transition-colors text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="p-5 pt-0 bg-gray-50">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-br from-emerald-900 to-emerald-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Precisa saber o valor do funeral agora?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Nossa equipe está disponível 24 horas para orientar, calcular os custos e apresentar um orçamento completo e transparente.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleWhatsAppClick('final')}
              className="flex items-center justify-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-emerald-900 px-8 py-4 rounded-lg text-lg font-bold transition-all shadow-xl"
            >
              <MessageCircle className="w-6 h-6" />
              <span>Solicitar orçamento pelo WhatsApp</span>
            </button>
            <button
              onClick={handlePhoneClick}
              className="flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/30 px-8 py-4 rounded-lg text-lg font-bold transition-all"
            >
              <Phone className="w-6 h-6" />
              <span>Ligar agora: {phoneNumber}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Botão Flutuante WhatsApp */}
      <button
        onClick={() => handleWhatsAppClick('flutuante')}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl z-50 transition-all hover:scale-110 animate-bounce"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </button>
    </div>
  );
};

export default ValorFuneralRJ;
